import { useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2 } from "lucide-react";
import { useCreatePersonnelProject } from "@/hooks/usePersonnelProjects";
import { CustomerSelectWithCreate } from "@/components/controller/CustomerSelectWithCreate";
import { VendorSelectWithCreate } from "@/components/controller/VendorSelectWithCreate";

interface RoleRow {
  role_name: string;
  bill_rate_regular: number;
  cost_rate: number;
  ot_threshold_hours: number;
  worker_count: number;
}

interface WorkerRow {
  rateIndex: number;
  worker_name: string;
  vendor_id: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

export function NewPersonnelProjectWizard({ open, onOpenChange }: Props) {
  const [step, setStep] = useState(1);
  const createMutation = useCreatePersonnelProject();

  // Step 1 — Project Info
  const [name, setName] = useState("");
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [clientPO, setClientPO] = useState("");
  const [nteAmount, setNteAmount] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [notes, setNotes] = useState("");

  // Step 2 — Rate Schedule
  const [otThreshold, setOtThreshold] = useState(40);
  const [roles, setRoles] = useState<RoleRow[]>([
    { role_name: "", bill_rate_regular: 0, cost_rate: 0, ot_threshold_hours: 40, worker_count: 1 },
  ]);

  // Step 3 — Workers
  const [workers, setWorkers] = useState<WorkerRow[]>([]);

  const addRole = () =>
    setRoles((r) => [...r, { role_name: "", bill_rate_regular: 0, cost_rate: 0, ot_threshold_hours: otThreshold, worker_count: 1 }]);

  const removeRole = (i: number) => setRoles((r) => r.filter((_, idx) => idx !== i));

  const updateRole = (i: number, key: keyof RoleRow, val: any) =>
    setRoles((r) => r.map((row, idx) => (idx === i ? { ...row, [key]: val } : row)));

  const goToStep3 = () => {
    // Generate worker rows from roles
    const wks: WorkerRow[] = [];
    roles.forEach((role, rateIndex) => {
      for (let w = 0; w < role.worker_count; w++) {
        wks.push({ rateIndex, worker_name: "", vendor_id: null });
      }
    });
    setWorkers(wks);
    setStep(3);
  };

  const updateWorker = (i: number, key: keyof WorkerRow, val: any) =>
    setWorkers((w) => w.map((row, idx) => (idx === i ? { ...row, [key]: val } : row)));

  const handleSubmit = async () => {
    await createMutation.mutateAsync({
      project: {
        name,
        customer_id: customerId,
        location_city: city || null,
        location_state: state || null,
        client_po_number: clientPO || null,
        nte_amount: nteAmount ? parseFloat(nteAmount) : null,
        start_date: startDate || null,
        end_date: endDate || null,
        notes: notes || null,
      } as any,
      rates: roles.map((r, i) => ({
        role_name: r.role_name,
        bill_rate_regular: r.bill_rate_regular,
        cost_rate: r.cost_rate,
        ot_threshold_hours: r.ot_threshold_hours,
        worker_count: r.worker_count,
        sort_order: i,
      })),
      workers: workers.filter((w) => w.worker_name.trim()),
    });
    onOpenChange(false);
    resetForm();
  };

  const resetForm = () => {
    setStep(1);
    setName("");
    setCustomerId(null);
    setCity("");
    setState("");
    setClientPO("");
    setNteAmount("");
    setStartDate("");
    setEndDate("");
    setNotes("");
    setRoles([{ role_name: "", bill_rate_regular: 0, cost_rate: 0, ot_threshold_hours: 40, worker_count: 1 }]);
    setWorkers([]);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) resetForm(); onOpenChange(v); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            New Personnel Project — Step {step} of 3
          </DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            <div>
              <Label>Project Name *</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Florida Admin Support" />
            </div>
            <div>
              <Label>Customer</Label>
              <CustomerSelectWithCreate value={customerId ?? ""} onValueChange={(v) => setCustomerId(v)} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>City</Label>
                <Input value={city} onChange={(e) => setCity(e.target.value)} />
              </div>
              <div>
                <Label>State</Label>
                <Input value={state} onChange={(e) => setState(e.target.value)} placeholder="FL" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Client PO Number</Label>
                <Input value={clientPO} onChange={(e) => setClientPO(e.target.value)} />
              </div>
              <div>
                <Label>NTE Amount</Label>
                <Input type="number" value={nteAmount} onChange={(e) => setNteAmount(e.target.value)} placeholder="Leave blank for open-ended" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </div>
              <div>
                <Label>End Date</Label>
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
            </div>
            <div className="flex justify-end">
              <Button disabled={!name.trim()} onClick={() => setStep(2)}>Next →</Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <Label>OT Threshold (hours/week):</Label>
              <Input
                type="number"
                className="w-24"
                value={otThreshold}
                onChange={(e) => {
                  const val = parseInt(e.target.value) || 40;
                  setOtThreshold(val);
                  setRoles((r) => r.map((row) => ({ ...row, ot_threshold_hours: val })));
                }}
              />
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Role Name</TableHead>
                  <TableHead>Bill Rate</TableHead>
                  <TableHead>OT Rate</TableHead>
                  <TableHead>Cost Rate</TableHead>
                  <TableHead className="w-20">Workers</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {roles.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Input value={r.role_name} onChange={(e) => updateRole(i, "role_name", e.target.value)} placeholder="e.g. Admin" />
                    </TableCell>
                    <TableCell>
                      <Input type="number" value={r.bill_rate_regular || ""} onChange={(e) => updateRole(i, "bill_rate_regular", parseFloat(e.target.value) || 0)} placeholder="$/HR" />
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      ${((r.bill_rate_regular || 0) * 1.5).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Input type="number" value={r.cost_rate || ""} onChange={(e) => updateRole(i, "cost_rate", parseFloat(e.target.value) || 0)} placeholder="$/HR" />
                    </TableCell>
                    <TableCell>
                      <Input type="number" className="w-16" value={r.worker_count} onChange={(e) => updateRole(i, "worker_count", parseInt(e.target.value) || 1)} min={1} />
                    </TableCell>
                    <TableCell>
                      {roles.length > 1 && (
                        <Button variant="ghost" size="icon" onClick={() => removeRole(i)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Button variant="outline" size="sm" onClick={addRole}>
              <Plus className="mr-1 h-3 w-3" /> Add Role
            </Button>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)}>← Back</Button>
              <Button
                disabled={roles.some((r) => !r.role_name.trim() || r.bill_rate_regular <= 0)}
                onClick={goToStep3}
              >
                Next →
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Assign workers to each role. Vendor is optional.</p>

            {roles.map((role, roleIdx) => {
              const roleWorkers = workers.filter((w) => w.rateIndex === roleIdx);
              return (
                <div key={roleIdx} className="space-y-2">
                  <h3 className="font-semibold text-sm">
                    {role.role_name || `Role ${roleIdx + 1}`} ({role.worker_count} workers) @ ${role.bill_rate_regular}/HR
                  </h3>
                  {roleWorkers.map((w) => {
                    const globalIdx = workers.indexOf(w);
                    return (
                      <div key={globalIdx} className="grid grid-cols-2 gap-2">
                        <Input
                          placeholder={`Worker ${roleWorkers.indexOf(w) + 1} name`}
                          value={w.worker_name}
                          onChange={(e) => updateWorker(globalIdx, "worker_name", e.target.value)}
                        />
                        <VendorSelectWithCreate
                          value={w.vendor_id ?? ""}
                          onValueChange={(v) => updateWorker(globalIdx, "vendor_id", v)}
                        />
                      </div>
                    );
                  })}
                </div>
              );
            })}

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(2)}>← Back</Button>
              <Button
                disabled={createMutation.isPending}
                onClick={handleSubmit}
              >
                {createMutation.isPending ? "Creating…" : "Create Project"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
